import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Switch, Route, Redirect } from 'react-router-dom';
import axios from 'axios';
import { useResizeDetector } from 'react-resize-detector';

import { routes } from 'routes';
import { RootState } from 'types';
import { Signup, Signin, ConfirmRegister, ResetPassword } from 'views/pages/Auth';
import { Dashboard } from 'views/pages/Dashboard';
import { License } from 'views/pages/License';
import { Plan } from 'views/pages/Plan';
import { UpgradeOnlinePlan } from 'views/pages/UpgradeOnlinePlan';
import { Profile, CreditCardDetails } from 'views/pages/User';
import { EnterprisePlan } from 'views/pages/EnterprisePlan';
import { Subclinics } from 'views/pages/Auth/Subclinics';

import {
  setCurrentCountry,
  setCurrentLanguage,
  setFirstStart,
  setIsLocationCheck,
  setWindowWidth
} from 'modules/app/actions';
import { ForgotPassword, CreateNewPassword } from 'views/pages/Auth';

import {setCountry, setCurrency, getCountries, getUserInfo} from 'modules/user/actions';
import {FromEmailPage} from "../startSubscriptionPage";
import {UpgradeOfflinePlan} from "../UpgradeOfflinePlan";
import {useQuery} from "../../../utils";
import {useTranslation} from "react-i18next";
import {UpgradeEUOnlinePlan} from "../UpgradeEUOnlinePlan";
import { getCountryFromLocalstorage } from 'utils/getCountry';

export const App = () => {
  const { token } = useSelector((state: RootState) => state.auth);
  const { userInfo, countriesList } = useSelector((state: RootState) => state.user);
  const { firstStart } = useSelector((state: RootState) => state.app);
  const dispatch = useDispatch();

  const { width, ref } = useResizeDetector();

  const success = (position: any) => {
    const latitude = position.coords.latitude;
    const longitude = position.coords.longitude;
    axios
      .get(
        `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}`,
      )
      .then(response => {
        const { data } = response;
        dispatch(setCountry(data.countryName));
      })
      .catch(error => {
        alert('Sorry. We could not check your location and set currency of your country.');
        dispatch(setIsLocationCheck(true));
        dispatch(setCurrency('aud'));
      })
      .finally(() => {});
  };

  const error = () => {
    alert('Unable to retrieve your location.');
    dispatch(setIsLocationCheck(true));
    dispatch(setCurrency('aud'));
  };

  const location = useQuery().get('location')?.toUpperCase();
  const { i18n } = useTranslation();

  useEffect(() => {
    dispatch(getCountries.request());
    dispatch(getUserInfo.request());
  }, []);



  useEffect(() => {
    if (location && countriesList && countriesList.length) {
      const country = countriesList.filter(i => i.short_name.toLowerCase() === location.toLowerCase());
      if (country && country.length) {
        dispatch(setCurrentLanguage(country[0].language_code));
        dispatch(setCurrentCountry(country[0].short_name));
        localStorage.setItem('lng', country[0].language_code);
        localStorage.setItem('country', country[0].short_name);
        i18n.changeLanguage(country[0].language_code);
      }
    }
    if (userInfo  && userInfo.country && userInfo.country.length && countriesList && countriesList.length) {
      const country = countriesList.filter(i => i.short_name.toLowerCase() === userInfo.country[0].short_name.toLowerCase());

      if (country && country.length) {
        const countryLocalstorage = getCountryFromLocalstorage();
        const langLocalstorage = localStorage.getItem('lng');
        const fCountry = countryLocalstorage || country[0].short_name;
        const fLang: any = country[0].language_code || langLocalstorage;
        dispatch(setCurrentLanguage(fLang));
        dispatch(setCurrentCountry(fCountry));
        localStorage.setItem('lng', fLang);
        localStorage.setItem('country', fCountry);
        i18n.changeLanguage(fLang);
      }
    }
  }, [location, countriesList, userInfo]);

  useEffect(() => {
    if (firstStart) {
      if (!navigator.geolocation) {
        alert('Geolocation is not supported by your browser.');
        dispatch(setIsLocationCheck(true));
        dispatch(setCurrency('aud'));
      } else {
        navigator.geolocation.getCurrentPosition(success, error);
      }
      dispatch(setFirstStart(false));
    }
  }, [firstStart]);

  useEffect(() => {
    if (width) {
      dispatch(setWindowWidth(width));
    }
  }, [width]);

  return (
    <div ref={ref}>
      <Switch>
        <Route
          exact
          path={routes.main}
          render={() => (token ? <Redirect to={routes.dashboard} /> : <Signin />)}
        />
        <Route
          path={`${routes.main}?`}
          render={() => (token ? <Redirect to={routes.dashboard} /> : <Signin />)}
        />
        <Route
          exact
          path={routes.signup}
          render={() => (token ? <Redirect to={routes.dashboard} /> : <Signup />)}
        />
        <Route
          exact
          path={routes.signin}
          render={() => (token ? <Redirect to={routes.dashboard} /> : <Signin />)}
        />
        <Route exact path={routes.dashboard} render={() => (token ? <Dashboard /> : <Signin />)} />
        <Route exact path={routes.licence} render={() => (token ? <License /> : <Signin />)} />
        <Route exact path={routes.plan} render={() => (token ? <Plan /> : <Signin />)} />
        <Route exact path={routes.startSubscription} render={() => (<FromEmailPage />)} />
        <Route exact path={routes.profile} render={() => (token ? <Profile /> : <Signin />)} />
        <Route exact path={routes.subclinics} render={() => <Subclinics />} />
        <Route
          exact
          path={routes.resetPassword}
          render={() => (token ? <ResetPassword /> : <Signin />)}
        />
        <Route
          exact
          path={routes.creditCardDetails}
          render={() => (token ? <CreditCardDetails /> : <Signin />)}
        />
        <Route exact path={routes.confirmRegister} render={() => <ConfirmRegister />} />
        <Route exact path={routes.upgradeOnlinePlan} render={() => <UpgradeOnlinePlan />} />
        <Route exact path={routes.upgradeEUOnlinePlan} render={() => <UpgradeEUOnlinePlan />} />
        <Route exact path={routes.upgradeOfflinePlan} render={() => <UpgradeOfflinePlan />} />
        <Route exact path={routes.enterprisePlan} render={() => <EnterprisePlan />} />
        <Route exact path={routes.forgotPassword} render={() => <ForgotPassword />} />
        <Route exact path={routes.createPassword} render={() => <CreateNewPassword />} />
      </Switch>
    </div>
  );
};
